import React, { memo, useCallback } from 'react';
import { AppLayout } from './AppLayout';
import { useLayout } from '@/contexts/LayoutContext';

export type PageLayoutType = 'default' | 'no-sidebar' | 'full-width';

interface PageWrapperProps {
  children: React.ReactNode;
  layout?: PageLayoutType;
  className?: string;
}

/**
 * PageWrapper - Composant utilitaire pour gérer les layouts spéciaux avec optimisations de performance
 * 
 * Ce composant permet de configurer facilement le type de layout
 * pour chaque page, en gérant automatiquement l'affichage ou non
 * de la sidebar selon le type de layout spécifié.
 * 
 * Types de layout supportés:
 * - 'default': Layout standard avec sidebar
 * - 'no-sidebar': Layout sans sidebar (Auth, Profile, ReferentForm)
 * - 'full-width': Layout pleine largeur sans sidebar
 * 
 * Fonctionnalités:
 * - Configuration automatique du layout selon le type
 * - Intégration avec le LayoutContext
 * - Support des classes CSS personnalisées
 * - Gestion automatique de la visibilité de la sidebar
 * - Memoization pour optimiser les performances
 * 
 * Exigences: 3.1, 6.1, 5.1, 5.3
 */
const PageWrapperComponent: React.FC<PageWrapperProps> = ({ 
  children, 
  layout = 'default',
  className = ''
}) => {
  const { setCurrentLayout } = useLayout();
  
  // Optimisation: utiliser useCallback pour éviter les re-créations
  const updateLayout = useCallback(() => {
    setCurrentLayout(layout);
  }, [layout, setCurrentLayout]);
  
  // Mettre à jour le layout dans le contexte
  React.useEffect(() => {
    updateLayout();
  }, [updateLayout]);
  
  // Pour les layouts sans sidebar, retourner directement le contenu
  if (layout === 'no-sidebar') {
    return (
      <div className={`page-wrapper-no-sidebar ${className}`} data-testid="page-wrapper-no-sidebar">
        {children}
      </div>
    );
  }
  
  // Pour les layouts full-width, retourner le contenu sans sidebar
  if (layout === 'full-width') {
    return (
      <div className={`page-wrapper-full-width ${className}`} data-testid="page-wrapper-full-width">
        {children}
      </div>
    );
  }
  
  // Pour le layout par défaut, utiliser AppLayout avec sidebar
  return (
    <AppLayout showSidebar={true}>
      <div className={`page-wrapper-default ${className}`} data-testid="page-wrapper-default">
        {children}
      </div>
    </AppLayout>
  );
};

// Memoization du composant pour éviter les re-renders inutiles
export const PageWrapper = memo(PageWrapperComponent, (prevProps, nextProps) => {
  return (
    prevProps.layout === nextProps.layout &&
    prevProps.className === nextProps.className &&
    prevProps.children === nextProps.children
  );
});